import { slicedToArray as _slicedToArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useCallback, useEffect } from 'react';
import { createEditor } from 'slate';
import { withReact, Slate, Editable } from 'slate-react';
import { withHistory } from 'slate-history';
import { Leaf } from './elements/leaf.js';
import { Element } from './elements/element.js';
import { withCustomNormalization, withLink, withImages } from './utils/plugins.js';
import { Toolbar } from './elements/toolbar.js';
import { prismCodeTheme } from './utils/prism-theme.js';
import { useDecorate, useOnKeydown, SetNodeToDecorations } from './utils/decorator.js';
import { contentToSlate, jsonToHtml, sanitizeHtml } from './utils/format-converters.js';
import unescape from 'lodash/unescape.js';
import { tokens } from '@everfi/design-system';
import { insertImage } from './utils/index.js';
import { Types } from './utils/contants.js';
import { Layout } from '../Layout/component.js';
var EMPTY_BLOCK = [{
  type: Types.paragraph,
  language: 'html',
  children: [{
    text: ''
  }]
}];
var RichTextEditor = function RichTextEditor(_ref) {
  var _ref$customButtons = _ref.customButtons,
    customButtons = _ref$customButtons === void 0 ? [] : _ref$customButtons,
    imagePath = _ref.imagePath,
    initialValue = _ref.initialValue,
    onChange = _ref.onChange,
    _ref$dataType = _ref.dataType,
    dataType = _ref$dataType === void 0 ? 'html' : _ref$dataType,
    _ref$shouldSanitize = _ref.shouldSanitize,
    shouldSanitize = _ref$shouldSanitize === void 0 ? false : _ref$shouldSanitize,
    _ref$allowCodeToggle = _ref.allowCodeToggle,
    allowCodeToggle = _ref$allowCodeToggle === void 0 ? true : _ref$allowCodeToggle,
    formElementName = _ref.formElementName;
  var _useState = useState(function () {
      return withCustomNormalization(withReact(withHistory(withLink(withImages(createEditor())))));
    }),
    _useState2 = _slicedToArray(_useState, 1),
    editor = _useState2[0];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isCode = _useState4[0],
    setIsCode = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    currentImagePath = _useState6[0],
    setCurrentImagePath = _useState6[1];
  var _useState7 = useState(initialValue || EMPTY_BLOCK),
    _useState8 = _slicedToArray(_useState7, 2),
    value = _useState8[0],
    setValue = _useState8[1];
  var _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    formValue = _useState0[0],
    setFormValue = _useState0[1];
  var renderLeaf = useCallback(function (props) {
    return /*#__PURE__*/React.createElement(Leaf, props);
  }, []);
  var renderElement = useCallback(function (props) {
    return /*#__PURE__*/React.createElement(Element, props);
  }, []);
  var decorate = useDecorate(editor);
  var onKeyDown = useOnKeydown(editor);
  var processDataForSave = function processDataForSave(value) {
    var valueToSave = value;
    if (dataType === 'html') {
      // convert it to html
      var html = unescape(jsonToHtml(editor));
      valueToSave = shouldSanitize ? sanitizeHtml(html) : html;
      setFormValue(valueToSave);
    }
    return valueToSave;
  };
  useEffect(function () {
    if (imagePath && imagePath !== currentImagePath) {
      insertImage(editor, imagePath);
      setCurrentImagePath(imagePath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePath, currentImagePath]);
  useEffect(function () {
    processDataForSave(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  useEffect(function () {
    if (typeof initialValue === 'string' && dataType === 'html' && typeof value === 'string') {
      // we got some straight up html from parent (how things have been usually stored). Need to convert it for display.
      setValue(contentToSlate(initialValue));
      setFormValue(value);
    }
  }, [initialValue, dataType, value]);
  var valueChanged = useCallback(function (newValue) {
    var valueToSave = newValue;
    var newValueToSave = processDataForSave(valueToSave);
    setValue(newValue); // local value is always a Descendant[] type for the editor
    if (onChange) onChange(newValueToSave);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dataType]);
  if (typeof value === 'string') return null;
  return /*#__PURE__*/React.createElement(Layout, {
    borderWidth: "1px",
    w: "100%",
    borderRadius: "md"
  }, /*#__PURE__*/React.createElement(Slate, {
    editor: editor,
    initialValue: value,
    onChange: valueChanged
  }, /*#__PURE__*/React.createElement(Toolbar, {
    isCode: isCode,
    setIsCode: setIsCode,
    customButtons: customButtons,
    allowCodeToggle: allowCodeToggle
  }), /*#__PURE__*/React.createElement(SetNodeToDecorations, null), /*#__PURE__*/React.createElement(Editable, {
    className: "rte-core",
    style: {
      padding: isCode ? '' : tokens.SpacingMd
    },
    decorate: decorate,
    renderElement: renderElement,
    renderLeaf: renderLeaf,
    onKeyDown: onKeyDown
  }), /*#__PURE__*/React.createElement("style", null, prismCodeTheme)), /*#__PURE__*/React.createElement("input", {
    name: formElementName,
    value: formValue,
    type: "hidden",
    className: "hidden"
  }));
};
RichTextEditor.displayName = "RichTextEditor";
export { RichTextEditor };